import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  usersService,
  AdminUserListItem,
  UserRole,
  UserStats,
  SignupPeriod,
} from '../../services/users.service'
import LoadingSpinner from '../../components/LoadingSpinner'
import BulkUploadModal from '../../components/admin/BulkUploadModal'

const ALL_ROLES: UserRole[] = [
  'FAMILY',
  'PROVIDER',
  'AMBULANCE_PROVIDER',
  'USTADZ',
  'CEMETERY_PROVIDER',
  'VENDOR',
  'ADMIN',
  'FINANCE',
  'OPERATOR_TRAINING',
  'OPERATOR_OPERATION',
]

function UserManagementPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [users, setUsers] = useState<AdminUserListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [stats, setStats] = useState<UserStats | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false)

  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })

  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: '',
    signupPeriod: '',
  })

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await usersService.getUsers({
        page,
        limit,
        search: filters.search || undefined,
        role: (filters.role as UserRole) || undefined,
        status: (filters.status as 'active' | 'inactive' | 'pending') || undefined,
        signupPeriod: (filters.signupPeriod as SignupPeriod) || undefined,
      })
      setUsers(data.data)
      setMeta(data.meta)
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [page, limit, filters])

  const fetchStats = useCallback(async () => {
    try {
      const s = await usersService.getUserStats()
      setStats(s)
    } catch {
      // non-critical
    }
  }, [])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setPage(1)
  }

  const resetFilters = () => {
    setFilters({ search: '', role: '', status: '', signupPeriod: '' })
    setPage(1)
  }

  const handleActivate = async (id: string) => {
    try {
      setActionLoading(id)
      await usersService.activateUser(id)
      await fetchUsers()
      await fetchStats()
    } catch {
      // toast handled by interceptor
    } finally {
      setActionLoading(null)
    }
  }

  const handleDeactivate = async (user: AdminUserListItem) => {
    const fullName = `${user.firstName} ${user.lastName}`.trim()
    const confirmed = window.confirm(
      t('userManagement.confirmDeactivate', {
        name: fullName || user.email,
        defaultValue: `Deactivate ${fullName || user.email}? They will not be able to log in until reactivated.`,
      }),
    )
    if (!confirmed) return
    try {
      setActionLoading(user.id)
      await usersService.deactivateUser(user.id)
      await fetchUsers()
      await fetchStats()
    } catch {
      // toast handled by interceptor
    } finally {
      setActionLoading(null)
    }
  }

  const getRoleBadgeClass = (role: UserRole) => {
    switch (role) {
      case 'ADMIN':
        return 'bg-purple-100 text-purple-800'
      case 'FINANCE':
        return 'bg-cyan-100 text-cyan-800'
      case 'FAMILY':
        return 'bg-blue-100 text-blue-800'
      case 'AMBULANCE_PROVIDER':
        return 'bg-emerald-100 text-emerald-800'
      case 'USTADZ':
        return 'bg-amber-100 text-amber-800'
      case 'CEMETERY_PROVIDER':
        return 'bg-teal-100 text-teal-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading && users.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {t('userManagement.title', 'User Management')}
            </h1>
            <p className="text-gray-600">
              {t('userManagement.subtitle', 'View and manage all registered users')}
            </p>
          </div>
          <button
            onClick={() => setBulkUploadOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0D7330] px-4 py-2 text-sm font-medium text-white hover:bg-[#0a5c27]"
          >
            {t('userManagement.bulkUpload.button', 'Bulk Upload')}
          </button>
        </div>

        <BulkUploadModal
          open={bulkUploadOpen}
          onClose={() => setBulkUploadOpen(false)}
          onCompleted={() => {
            fetchUsers()
            fetchStats()
          }}
        />

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <StatCard label={t('userManagement.total', 'Total')} value={stats.total} color="text-gray-900" />
            <StatCard label={t('userManagement.active', 'Active')} value={stats.active} color="text-green-600" />
            <StatCard label={t('userManagement.inactive', 'Inactive')} value={stats.inactive} color="text-red-600" />
            <StatCard label={t('userManagement.pending', 'Pending Approval')} value={stats.pending} color="text-amber-600" />
            <StatCard label={t('userManagement.verified', 'Verified')} value={stats.verified} color="text-blue-600" />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('userManagement.search', 'Search')}
              </label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder={t('userManagement.searchPlaceholder', 'Email, name, or member ID')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('userManagement.role', 'Role')}
              </label>
              <select
                value={filters.role}
                onChange={(e) => handleFilterChange('role', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
              >
                <option value="">{t('userManagement.allRoles', 'All Roles')}</option>
                {ALL_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {t(`userManagement.roles.${r}`, r)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('userManagement.status', 'Status')}
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
              >
                <option value="">{t('userManagement.allStatuses', 'All Statuses')}</option>
                <option value="active">{t('userManagement.active', 'Active')}</option>
                <option value="inactive">{t('userManagement.inactive', 'Inactive')}</option>
                <option value="pending">{t('userManagement.pending', 'Pending Approval')}</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={resetFilters}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('userManagement.reset', 'Reset')}
              </button>
            </div>
          </div>

          {/* Signup window chips — counts come from stats, clicking filters the list */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-700">
              {t('userManagement.signups', 'Signups')}:
            </span>
            {(
              [
                ['', t('userManagement.allSignups', 'All'), stats?.total],
                ['today', t('userManagement.today', 'Today'), stats?.signups?.today],
                ['7d', t('userManagement.last7Days', 'Last 7 days'), stats?.signups?.last7Days],
                ['30d', t('userManagement.last30Days', 'Last 30 days'), stats?.signups?.last30Days],
              ] as const
            ).map(([value, label, count]) => (
              <button
                key={value || 'all'}
                onClick={() => handleFilterChange('signupPeriod', value)}
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  filters.signupPeriod === value
                    ? 'bg-[#0D7330] text-white'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                {label}
                {count !== undefined && ` (${count})`}
              </button>
            ))}
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                    {t('userManagement.memberId', 'Member ID')}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                    {t('userManagement.name', 'Name')}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                    {t('userManagement.role', 'Role')}
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                    {t('userManagement.status', 'Status')}
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                    {t('userManagement.flags', 'Verified / Approved')}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                    {t('userManagement.registered', 'Registered')}
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">
                    {t('userManagement.actions', 'Actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      {t('userManagement.noUsers', 'No users found')}
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-mono text-gray-700">
                        {user.memberId}
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                          {user.phone && (
                            <p className="text-xs text-gray-400">{user.phone}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeClass(
                            user.role,
                          )}`}
                        >
                          {t(`userManagement.roles.${user.role}`, user.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            user.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {user.isActive
                            ? t('userManagement.active', 'Active')
                            : t('userManagement.inactive', 'Inactive')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2 text-xs">
                          <FlagBadge
                            ok={user.isVerified}
                            okLabel={t('userManagement.verified', 'Verified')}
                            notLabel={t('userManagement.unverified', 'Unverified')}
                          />
                          <FlagBadge
                            ok={user.isApproved}
                            okLabel={t('userManagement.approved', 'Approved')}
                            notLabel={t('userManagement.notApproved', 'Not approved')}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                            className="px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50"
                          >
                            {t('userManagement.view', 'View')}
                          </button>
                          {user.isActive ? (
                            <button
                              onClick={() => handleDeactivate(user)}
                              disabled={actionLoading === user.id || user.role === 'ADMIN'}
                              className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                              title={
                                user.role === 'ADMIN'
                                  ? t('userManagement.cannotDeactivateAdmin', 'Admin accounts cannot be deactivated')
                                  : undefined
                              }
                            >
                              {actionLoading === user.id
                                ? '...'
                                : t('userManagement.deactivate', 'Deactivate')}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleActivate(user.id)}
                              disabled={actionLoading === user.id}
                              className="px-3 py-1.5 text-xs font-medium text-green-600 border border-green-300 rounded-lg hover:bg-green-50 disabled:opacity-40"
                            >
                              {actionLoading === user.id
                                ? '...'
                                : t('userManagement.activate', 'Activate')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {meta.totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                {t('userManagement.showing', 'Showing')}{' '}
                {Math.min((meta.page - 1) * meta.limit + 1, meta.total)}-
                {Math.min(meta.page * meta.limit, meta.total)} {t('userManagement.of', 'of')}{' '}
                {meta.total}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={!meta.hasPrev}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('userManagement.previous', 'Previous')}
                </button>
                <span className="text-sm text-gray-600">
                  {t('userManagement.page', 'Page')} {meta.page} {t('userManagement.of', 'of')}{' '}
                  {meta.totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={!meta.hasNext}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('userManagement.next', 'Next')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-white rounded-xl shadow-md p-4">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
    </div>
  )
}

function FlagBadge({
  ok,
  okLabel,
  notLabel,
}: {
  ok: boolean
  okLabel: string
  notLabel: string
}) {
  return (
    <span
      className={`px-2 py-0.5 rounded-full ${
        ok ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
      }`}
    >
      {ok ? `✓ ${okLabel}` : notLabel}
    </span>
  )
}

export default UserManagementPage
